/**
 * Review Type Definitions for Skillzen Platform
 * Course Feedback & Review System
 * Matches backend DTOs and enums exactly
 */

// ============================================================================
// Enums (matching backend)
// ============================================================================

/**
 * ReviewType - Defines what entity the review is for
 */
export enum ReviewType {
  COURSE = 'course',
  CERTIFICATE = 'certificate',
  LEARNING_PATH = 'learning_path',
}

/**
 * ReviewStatus - Tracks the moderation status of a review
 * APPROVED - Review visible to others (default)
 * FLAGGED - Auto-flagged when report_count reaches threshold
 * DELETED - Soft deleted by admin
 */
export enum ReviewStatus {
  APPROVED = 'approved',
  FLAGGED = 'flagged',
  DELETED = 'deleted',
}

// Admin Response Object

export interface AdminResponse {
  text: string;
  responded_by: string;
  responded_at: string;
}

// ============================================================================
// Review Entity (matches ReviewResponseDto)
// ============================================================================

export interface Review {
  _id: string;
  review_type: ReviewType;
  course_id?: string;
  certification_id?: string;
  learning_path_id?: string;
  user_id: string;
  rating: number;
  review_text: string;
  status: ReviewStatus;
  is_verified_completion: boolean;
  is_featured: boolean;
  helpful_count: number;
  helpful_by?: string[];
  report_count: number;
  admin_response?: AdminResponse;
  createdAt: string;
  updatedAt: string;

  // Populated user data (may or may not be present)
  user_details?: {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    empl_id?: string | null;
    initials?: string;
    profile_url?: string | null;
  };

  // For current user context
  has_marked_helpful?: boolean;
  has_reported?: boolean;

  // Entity title (for display)
  entity_title?: string;

  // Entity name from admin API (course/certification/learning path name)
  entity_name?: string;
}

// ============================================================================
// Review Statistics (matches ReviewStatsResponseDto)
// ============================================================================

export interface RatingBreakdown {
  5: number;
  4: number;
  3: number;
  2: number;
  1: number;
}

export interface ReviewStats {
  avg_rating: number;
  total_reviews: number;
  rating_breakdown: RatingBreakdown;
}

// Bulk stats response for list pages 
export interface BulkReviewStatsResponse {
  review_type: ReviewType;
  stats: { [entity_id: string]: ReviewStats };
  total_entities: number;
}


// Request DTOs


/**
 * CreateReviewDto - POST /api/reviews/learner
 */
export interface CreateReviewRequest {
  review_type: ReviewType;
  course_id?: string;
  certification_id?: string;
  learning_path_id?: string;
  rating: number;
  review_text?: string;
}

/**
 * UpdateReviewDto - PUT /api/reviews/learner/:review_id
 */
export interface UpdateReviewRequest {
  rating?: number;
  review_text?: string;
}

/**
 * ReportReviewDto - POST /api/reviews/learner/:review_id/report
 */
export interface ReportReviewRequest {
  reason: string;
}

/**
 * UpdateReviewStatusDto - PUT /api/reviews/admin/:review_id/status
 */
export interface UpdateReviewStatusRequest {
  status: ReviewStatus;
}

/**
 * AddAdminResponseDto - POST /api/reviews/admin/:review_id/response
 */
export interface AddAdminResponseRequest {
  response_text: string;
}

/**
 * ToggleFeaturedDto - PUT /api/reviews/admin/:review_id/feature
 */
export interface ToggleFeaturedRequest {
  is_featured: boolean;
}

/**
 * GetReviewsListDto - query params for admin list
 */
export interface AdminReviewListParams {
  skip?: number;
  limit?: number;
  review_type?: ReviewType;
  status?: ReviewStatus;
  rating?: number;
  is_deleted?: boolean;
  course_id?: string;
  certification_id?: string;
  learning_path_id?: string;
  search?: string;
}

/**
 * Query params for public reviews
 */
export interface ReviewListParams {
  skip?: number;
  limit?: number;
}


// Response DTOs (matching backend)


/**
 * GetReviewsListResponseDto
 */
export interface ReviewListResponse {
  reviews: Review[];
  total_count: number;
}

/**
 * MarkHelpfulResponseDto
 */
export interface MarkHelpfulResponse {
  review_id: string;
  helpful_count: number;
  is_helpful: boolean;
}


// API Response Wrapper


export interface ApiResponse<T> {
  success: boolean;
  code: number;
  description: string;
  data: T;
}

// ============================================================================
// Admin Dashboard Metrics
// ============================================================================

export interface AdminDashboardMetrics {
  total_reviews: number;
  approved_count: number;
  flagged_count: number;
}


// Form Data (for React Hook Form)


export interface ReviewFormData {
  rating: number;
  review_text: string;
}

export interface AdminResponseFormData {
  response_text: string;
}


// Rating Labels


export const RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Amazing',
  5: 'Excellent',
};

export const getRatingLabel = (rating: number): string => {
  return RATING_LABELS[Math.round(rating)] || '';
};
